import {ClientDto} from '../dtos/client.dto';
import {ClientContactDto} from '../dtos/client-contact.dto';
import {addressLine, ContactEntry, entriesFrom} from './contact-entries';

/** A client the search found, and the Additional Client Info entry that matched when nothing of their own did. */
export interface ClientHit {
  client: ClientDto;
  matched: ContactEntry | null;
}

/**
 * Add New Appointment's client search (SQ-666), Android's searchClients
 * (ClientContent.kt): anything tied to a client (name, phone, email, address:
 * street, city, state, zip) and every entry under their Additional Client
 * Info, titles included ("Wife's cell"). Case-insensitive substring; typed
 * digits also match a phone however it is punctuated. Clients found on their
 * own details come first; one found only through an additional entry
 * carries that entry, for the result line to show.
 */
export const searchClients = (query: string, clients: ClientDto[], contacts: ClientContactDto[], limit = 6): ClientHit[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const digits = needle.replace(/\D/g, '');
  const matches = (text?: string | null) => {
    if (!text || !text.trim()) return false;
    const t = text.toLowerCase();
    return t.includes(needle) || (digits.length >= 3 && t.replace(/\D/g, '').includes(digits));
  };
  const own = clients.filter(c =>
    [c.name, c.phoneNumber, c.email, c.address, c.city, c.state, c.postalCode].some(matches)
    || matches([c.address, c.city, c.state, c.postalCode].filter(Boolean).join(' ')));
  const ownIds = new Set(own.map(c => c.id));
  const byClient = new Map<number, ClientContactDto[]>();
  contacts.forEach(row => byClient.set(row.clientId, [...(byClient.get(row.clientId) ?? []), row]));
  const throughEntries: ClientHit[] = [];
  clients.filter(c => !ownIds.has(c.id)).forEach(client => {
    const matched = entriesFrom(byClient.get(client.id) ?? [])
      .find(e => matches(e.title) || matches(e.value) || (e.kind === 'address' && matches(addressLine(e))));
    if (matched) throughEntries.push({client, matched});
  });
  return [...own.map(client => ({client, matched: null})), ...throughEntries].slice(0, limit);
};
